type SortChangeDetail = { value: string };

/* eslint-disable @typescript-eslint/naming-convention */
// We are augmenting built-in DOM interfaces; their names are fixed.
declare global {
  interface Window {
    SortOptions?: { set: (value: string, trigger?: boolean) => boolean };
    CSS?: { escape?: (s: string) => string };
    performSearch?: (o: { sort?: string }) => void;
  }
  interface DocumentEventMap {
    "sort:change": CustomEvent<SortChangeDetail>;
  }
  interface WindowEventMap {
    "sort:change": CustomEvent<SortChangeDetail>;
  }
}
/* eslint-enable @typescript-eslint/naming-convention */

const selectors = {
  root: ".sort-options",
  btn: ".sort-options__button",
  text: ".sort-options__text",
  newPanel: ".sort-options__options",
  newOptBtn: ".sort-options__option-button",
} as const;

const ENABLE_RELOAD_FALLBACK = false;

function cssEscape(val: string) {
  return typeof window.CSS?.escape === "function" ? window.CSS.escape(val) : val.replace(/"/g, '\\"');
}

function getPanel(root: HTMLElement) {
  const newPanel = root.querySelector<HTMLElement>(selectors.newPanel);
  return { newPanel };
}

function toggle(root: HTMLElement, open?: boolean) {
  const btn = root.querySelector<HTMLButtonElement>(selectors.btn);
  const { newPanel } = getPanel(root);
  const panel = newPanel;
  if (!btn || !panel) return;

  const willOpen = open ?? (btn.getAttribute("aria-expanded") !== "true");
  btn.setAttribute("aria-expanded", willOpen ? "true" : "false");
  panel.hidden = !willOpen;
}

function closeAllExcept(except?: HTMLElement) {
  document.querySelectorAll<HTMLElement>(selectors.root).forEach((root) => {
    if (except && root === except) return;
    const btn = root.querySelector<HTMLButtonElement>(selectors.btn);
    const { newPanel } = getPanel(root);
    const panel = newPanel;
    if (btn) btn.setAttribute("aria-expanded", "false");
    if (panel) panel.hidden = true;
  });
}

function findOptionByValue(root: HTMLElement, value: string): { el: HTMLElement; label: string } | null {
  // Try new markup first
  const newBtn = root.querySelector<HTMLButtonElement>(`${selectors.newOptBtn}[data-value="${cssEscape(value)}"]`);
  if (newBtn) {
    const label = newBtn.dataset.label || newBtn.textContent?.trim() || value;
    return { el: newBtn, label };
  }

  return null;
}

function markSelectedUI(root: HTMLElement, value: string) {
  // New markup: aria-selected on list items containing buttons
  root.querySelectorAll<HTMLElement>('[role="option"]').forEach((li) => {
    const b = li.querySelector<HTMLButtonElement>(selectors.newOptBtn);
    li.setAttribute("aria-selected", String(b?.dataset.value === value));
  });
}

function runSortChange(value: string) {
  if (window.performSearch)
  {
    window.performSearch({ sort: value });
    return;
  }

  document.dispatchEvent(
    new CustomEvent<SortChangeDetail>("sort:change", {
      detail: { value },
      bubbles: true,
      composed: true,
    }));

  if (ENABLE_RELOAD_FALLBACK)
  {
    location.replace(String(new URL(location.href)));
  }
}

function selectOption(root: HTMLElement, value: string, label: string, { silent = false } = {}) {
  const btn = root.querySelector<HTMLButtonElement>(selectors.btn);
  const text = root.querySelector<HTMLElement>(selectors.text);

  if (!btn || !text) {
    return;
  }

  text.textContent = label;
  btn.dataset.value = value;
  markSelectedUI(root, value);

  // Sync URL
  const url = new URL(window.location.href);
  url.searchParams.set("sort", value);
  window.history.replaceState({}, "", url.toString());

  if (!silent) runSortChange(value);

  // Close any open panel
  const { newPanel } = getPanel(root);
  const panel = newPanel;

  if (panel) {
    btn.setAttribute("aria-expanded", "false");
    panel.hidden = true;
  }
}

function setSortValue(value: string, trigger = true) {
  const root = document.querySelector<HTMLElement>(selectors.root);

  if (!root) {
    return false;
  }

  const found = findOptionByValue(root, value);

  if (!found) {
    return false;
  }

  selectOption(root, value, found.label, { silent: !trigger });
  return true;
}

function initFromUrl(root: HTMLElement) {
  const sortInUrl = new URL(location.href).searchParams.get("sort");

  if (!sortInUrl) {
    return;
  }

  const found = findOptionByValue(root, sortInUrl);
  if (found) {
    // Update UI without triggering another search
    selectOption(root, sortInUrl, found.label, { silent: true });
  }
}

export const useSortSearch = () => {
  // Click handling (new & legacy)
  document.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement;
    const root = target.closest<HTMLElement>(selectors.root);

    if (!root) {
      closeAllExcept();
      return;
    }

    const btn = target.closest<HTMLButtonElement>(selectors.btn);
    if (btn) {
      toggle(root);
      return;
    }

    // New option buttons
    const optBtn = target.closest<HTMLButtonElement>(selectors.newOptBtn);
    if (optBtn) {
      const value = optBtn.dataset.value || "";
      const label = optBtn.dataset.label || optBtn.textContent?.trim() || value;
      selectOption(root, value, label);
      return;
    }
  });

  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape") closeAllExcept();
  });

  // Initialize from URL once DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      document.querySelectorAll<HTMLElement>(selectors.root).forEach(initFromUrl);
    });
  } else {
    document.querySelectorAll<HTMLElement>(selectors.root).forEach(initFromUrl);
  }
};

// auto-init
useSortSearch();

// Public helper:
window.SortOptions = {
  set: (value: string, trigger = true) => setSortValue(value, trigger),
};
